'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Menu, Search, Bell, Settings, ChevronDown } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface FeedTopbarProps {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}

export default function FeedTopbar({ onToggleSidebar }: FeedTopbarProps) {
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 border-b border-border bg-background/90 backdrop-blur-xl flex items-center px-4 gap-3">
      {/* Toggle + Logo */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          onClick={onToggleSidebar}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          aria-label="Toggle sidebar"
        >
          <Menu size={18} />
        </button>
        <Link href="/" className="flex items-center gap-2 group">
          <AppLogo size={26} />
          <span className="font-sans text-lg font-800 tracking-widest text-foreground hidden sm:block group-hover:text-accent transition-colors">
            NEXUS
          </span>
        </Link>
      </div>

      {/* Search */}
      <div className="flex-1 max-w-lg mx-auto">
        <div className={`relative flex items-center rounded-xl border transition-all duration-150 ${searchFocused ? 'border-primary bg-input' : 'border-border bg-muted/50'}`}>
          <Search size={15} className="absolute left-3 text-muted-foreground" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search people, topics, posts..."
            className="w-full bg-transparent pl-9 pr-4 py-2 text-sm text-foreground placeholder-muted-foreground outline-none"
          />
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Notifications */}
        <button className="relative w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150">
          <Bell size={17} />
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary border border-background" />
        </button>

        {/* Settings */}
        <button className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150">
          <Settings size={17} />
        </button>

        {/* Avatar */}
        <button className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl border border-border hover:border-primary/40 hover:bg-muted transition-all duration-150">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white text-xs font-700">
            RK
          </div>
          <span className="hidden md:block text-sm font-500 text-foreground">Riya</span>
          <ChevronDown size={13} className="text-muted-foreground hidden md:block" />
        </button>
      </div>
    </header>
  );
}